import { createElement } from 'react';
import { flow, pipe } from 'fp-ts/function';
import { sortBy } from 'fp-ts/Array';
import { Ord } from 'fp-ts/Ord';
import { foldl } from '@/util/fp';
import { MASTER_TABLE_SCHEMA } from '@/util/TableRef';
import { escapeRegexString } from '@/util/escapeRegexString';
import {
  GroupedColumnEntitiesBySearch,
  ColumnEntity,
  SchemaName,
  SchemaNameTablesPair,
  TableEntity,
} from '../types';

const SEARCH_REGEX_FLAGS = 'gi';

export const getMatchingPredicateFor =
  (searchTerms: string[]) => (name: string) =>
    searchTerms.every((searchTerm) =>
      new RegExp(escapeRegexString(searchTerm), SEARCH_REGEX_FLAGS).test(name),
    );

export const wrapMatchingParts = (entityName: string, searchTerms: string[]) =>
  searchTerms.length
    ? entityName.replace(
        new RegExp(
          searchTerms.map(escapeRegexString).join('|'),
          SEARCH_REGEX_FLAGS,
        ),
        '<span class="sHighlight">$&</span>',
      )
    : entityName;

const transformIntoSpanElement = (__html: string) =>
  createElement('span', { dangerouslySetInnerHTML: { __html } });

export const highlightSearch = flow(
  wrapMatchingParts,
  transformIntoSpanElement,
);

export const getColumnName = (column: ColumnEntity) => column.name();

export const getColumnEntities = (table: TableEntity) => table.columns();

export const getFullTableName = (table: TableEntity) =>
  `${table.schema()}:${table.table()}`;

const groupTableEntitiesBySchemaName = (
  grouped: Record<SchemaName, TableEntity[]>,
  table: TableEntity,
) => {
  const schemaName = table.schema();

  if (!grouped[schemaName]) grouped[schemaName] = [];

  grouped[schemaName].push(table);

  return grouped;
};

const masterTableSchemaFirstOrd: Ord<SchemaNameTablesPair> = {
  equals: ([a], [b]) => a === b,
  compare: ([a], [b]) =>
    a === MASTER_TABLE_SCHEMA ? -1 : b === MASTER_TABLE_SCHEMA ? 1 : 0,
};

export const groupBySchemaName: F<TableEntity[], SchemaNameTablesPair[]> = (
  tableEntities,
) =>
  pipe(
    tableEntities,
    foldl(groupTableEntitiesBySchemaName, {}),
    Object.entries,
    sortBy([masterTableSchemaFirstOrd]),
  );

const groupColumnEntitiesBySearch =
  (predicate: Predicate<ColumnEntity>) =>
  (grouped: GroupedColumnEntitiesBySearch, column: ColumnEntity) => {
    if (predicate(column)) {
      grouped.matching.push(column);
    } else {
      grouped.mismatching.push(column);
    }
    return grouped;
  };

export const groupColumnsBy = (
  searchPredicate: Predicate<ColumnEntity>,
  entities: ColumnEntity[],
) =>
  foldl(groupColumnEntitiesBySearch(searchPredicate), {
    matching: [],
    mismatching: [],
  })(entities);
